/**
 * Recipe-specific typography components optimized for coffee content
 */

import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, TextProps } from '../Text';
import { useTheme } from '../../themes';
import { colorTokens } from '../../tokens';

export interface RecipeTextProps extends Omit<TextProps, 'variant'> {
  children: React.ReactNode;
}

export interface RecipeStepProps extends RecipeTextProps {
  stepNumber?: number;
  showNumber?: boolean;
}

export interface RecipeIngredientProps
  extends Omit<RecipeTextProps, 'children'> {
  measurement?: string;
  ingredient: string;
  notes?: string;
}

/**
 * Recipe title component with optimized styling for recipe names
 */
export function RecipeTitle({ children, ...props }: RecipeTextProps) {
  return (
    <Text variant='recipeTitle' {...props} accessibilityRole='header'>
      {children}
    </Text>
  );
}

/**
 * Recipe step component with optional step numbering
 */
export function RecipeStep({
  children,
  stepNumber,
  showNumber = true,
  style,
  ...props
}: RecipeStepProps) {
  const { theme } = useTheme();

  if (!showNumber || stepNumber === undefined) {
    return (
      <Text variant='recipeStep' style={style} {...props}>
        {children}
      </Text>
    );
  }

  const containerStyle = [
    styles.stepContainer,
    { marginBottom: theme.spacing.spacing[3] },
    style,
  ];

  const numberStyle = [
    styles.stepNumber,
    {
      backgroundColor: colorTokens.primary[500],
      color: theme.colors.text.inverse,
      width: theme.spacing.spacing[8],
      height: theme.spacing.spacing[8],
      borderRadius: theme.spacing.spacing[4],
      marginRight: theme.spacing.spacing[3],
    },
  ];

  return (
    <View style={containerStyle}>
      <View style={numberStyle}>
        <Text
          variant='button'
          color='inverse'
          align='center'
          style={styles.stepNumberText}
        >
          {stepNumber}
        </Text>
      </View>
      <View style={styles.stepContent}>
        <Text variant='recipeStep' {...props}>
          {children}
        </Text>
      </View>
    </View>
  );
}

/**
 * Recipe ingredient component with measurement and notes
 */
export function RecipeIngredient({
  measurement,
  ingredient,
  notes,
  style,
  ...props
}: RecipeIngredientProps) {
  const { theme } = useTheme();

  const containerStyle = [
    styles.ingredientContainer,
    { marginBottom: theme.spacing.spacing[2] },
    style,
  ];

  return (
    <View style={containerStyle}>
      <View style={styles.ingredientMain}>
        {measurement && (
          <Text
            variant='recipeMeasurement'
            color='primary'
            style={[
              styles.measurement,
              { minWidth: theme.spacing.spacing[16] },
            ]}
          >
            {measurement}
          </Text>
        )}
        <Text
          variant='recipeIngredient'
          color='primary'
          style={styles.ingredientName}
          {...props}
        >
          {ingredient}
        </Text>
      </View>
      {notes && (
        <Text
          variant='caption'
          color='tertiary'
          style={[
            styles.ingredientNotes,
            {
              marginLeft: measurement
                ? theme.spacing.spacing[16] + theme.spacing.spacing[3]
                : 0,
            },
          ]}
        >
          {notes}
        </Text>
      )}
    </View>
  );
}

/**
 * Recipe time component for duration display
 */
export function RecipeTime({ children, ...props }: RecipeTextProps) {
  const { theme } = useTheme();

  return (
    <Text
      variant='recipeMeasurement'
      color='secondary'
      style={[
        styles.recipeTime,
        {
          backgroundColor:
            theme.colors.surface?.secondary ||
            theme.colors.background.secondary,
          paddingHorizontal: theme.spacing.spacing[2],
          paddingVertical: theme.spacing.spacing[1],
          borderRadius: theme.spacing.spacing[1],
        },
      ]}
      {...props}
    >
      {children}
    </Text>
  );
}

/**
 * Recipe yield component for serving information
 */
export function RecipeYield({ children, ...props }: RecipeTextProps) {
  return (
    <Text variant='recipeMeasurement' color='secondary' {...props}>
      {children}
    </Text>
  );
}

/**
 * Recipe difficulty component
 */
export function RecipeDifficulty({ children, ...props }: RecipeTextProps) {
  const { theme } = useTheme();

  const getDifficultyColor = (difficulty: string) => {
    const lower = difficulty.toLowerCase();
    if (lower.includes('easy') || lower.includes('beginner')) {
      return colorTokens.semantic.success.DEFAULT;
    }
    if (lower.includes('hard') || lower.includes('expert')) {
      return colorTokens.semantic.error.DEFAULT;
    }
    return colorTokens.semantic.warning.DEFAULT;
  };

  return (
    <Text
      variant='label'
      style={[
        styles.difficulty,
        {
          color: getDifficultyColor(children as string),
          backgroundColor: `${getDifficultyColor(children as string)}20`,
          paddingHorizontal: theme.spacing.spacing[2],
          paddingVertical: theme.spacing.spacing[1],
          borderRadius: theme.spacing.spacing[1],
        },
      ]}
      {...props}
    >
      {children}
    </Text>
  );
}

/**
 * Recipe equipment component
 */
export function RecipeEquipment({ children, ...props }: RecipeTextProps) {
  return (
    <Text variant='recipeIngredient' color='secondary' {...props}>
      {children}
    </Text>
  );
}

const styles = StyleSheet.create({
  stepContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  stepNumber: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stepNumberText: {
    lineHeight: undefined, // Let the container handle centering
  },
  stepContent: {
    flex: 1,
    paddingTop: 2, // Slight adjustment for visual alignment
  },
  ingredientContainer: {
    // Container for ingredient with optional notes
  },
  ingredientMain: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  measurement: {
    textAlign: 'right',
    flexShrink: 0,
  },
  ingredientName: {
    flex: 1,
    marginLeft: 12,
  },
  ingredientNotes: {
    marginTop: 2,
    fontStyle: 'italic',
  },
  recipeTime: {
    textAlign: 'center',
  },
  difficulty: {
    textAlign: 'center',
    fontWeight: '600',
    textTransform: 'uppercase',
    fontSize: 11,
    letterSpacing: 0.5,
  },
});
